import React, { useCallback, useMemo } from 'react'
import ZionForm, { useZionForm, FormAutosuggest, useWatch } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash.isempty'
import uniqBy from 'lodash.uniqby'
import { InfoSheetPanel, Button, useOverlay, Separator } from '@fs/zion-ui'
import {
  getNestedOrFloatingElementById,
  useSlsStateContext,
  getEventLabel,
  getPersonName,
  generateSls,
  getNestedSubelementsByType,
  getMostRecentFieldValue,
  getImage,
} from '@fs/zion-sls'
import PanelHeader from '../shared/PanelHeader'
import SaveComponent from '../shared/SaveComponent'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import Tip from '../../../tip/Tip'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import Event from '../../lifeEvents/Event'
import { parseEvent } from './utils/eventUtils'

const validate = (values, t) => {
  const errors = {}
  if (isEmpty(values.eventId)) {
    errors.eventId = {
      type: 'required',
      message: t('link-existing-event.event-required', 'Event is required'),
    }
  }

  return {
    values,
    errors,
  }
}

/**
 * Generic panel for selecting an event from a list of events on this record and image to add to the selected person.
 * @param {Object} person - selected person
 * @param {Boolean} shouldSaveToSls - if this is false, an onSave function should be provided to accept the form data and dispatches.
 * @param {String} appName - name of the app that is using this component.
 * @param {Function} onSave - callback to be called when save is clicked.
 * @returns {Element}
 */

const LinkExistingEventPanel = ({ person, shouldSaveToSls, appName, onSave = () => {} }) => {
  if (!appName) throw new Error('LinkExistingEventPanel: appName is required parameter')

  const [t] = useTranslation()
  const { openOverlay, closeOverlay, linkExistingEventOverlay } = useOverlayContext()
  const zionSlsState = useSlsStateContext()
  const currentRecord = getNestedOrFloatingElementById(zionSlsState.images, person?.recordId)
  const currentImage = getImage(zionSlsState.images, zionSlsState.currentImageArk)
  const personName = getPersonName({ person })
  const saveChangesOverlay = useOverlay()

  const eventSuggestions = useMemo(() => {
    const getSecondaryText = (event) => {
      const date = getNestedSubelementsByType(event, 'DATE', true)
      const place = getNestedSubelementsByType(event, 'PLACE', true)
      const dateFieldValue = getMostRecentFieldValue(date[0])
      const placeFieldValue = getMostRecentFieldValue(place[0])
      return [dateFieldValue, placeFieldValue].filter(Boolean).join(' | ')
    }

    const filterFunc = (ev) => {
      const attachedToThisPersonAlready = ev.superElements.find((supEl) => supEl.id === person?.id)
      const isCoupleEvent = ev.typeData.elementTypes.includes('RELATIONSHIP')
      return !attachedToThisPersonAlready && !isCoupleEvent
    }

    const inThisRecord =
      getNestedSubelementsByType(currentRecord, 'EVENT')
        .filter(filterFunc)
        .map((event) => ({
          primaryText: getEventLabel(event, zionSlsState.types),
          secondaryText: getSecondaryText(event),
          key: event.id,
          group: t('link-existing-event.event-in-this-record', 'IN THIS RECORD'),
        })) || []

    const inThisImage =
      getNestedSubelementsByType(currentImage, 'EVENT')
        .filter(filterFunc)
        .map((event) => ({
          primaryText: getEventLabel(event, zionSlsState.types),
          secondaryText: getSecondaryText(event),
          key: event.id,
          group: t('link-existing-event.event-in-this-image', 'IN THIS IMAGE'),
        })) || []

    const floatingPersons = getNestedSubelementsByType(currentImage.floatingElements, 'PERSON')
    const fromFloating =
      floatingPersons.flatMap((fp) => {
        return getNestedSubelementsByType(fp, 'EVENT')
          .filter(filterFunc)
          .map((event) => ({
            primaryText: getEventLabel(event, zionSlsState.types),
            secondaryText: getSecondaryText(event),
            key: event.id,
            group: t('link-existing-event.event-in-this-image', 'IN THIS IMAGE'),
          }))
      }) || []

    return uniqBy(inThisRecord.concat(inThisImage).concat(fromFloating), (ev) => ev.key)
  }, [currentImage, currentRecord, zionSlsState.types, person?.id, t])

  const formMethods = useZionForm({
    defaultValues: { eventId: null },
    mode: 'all',
    resolver: (values) => validate(values, t),
  })
  useUnsavedChangesOverlay({ methods: formMethods, overlay: saveChangesOverlay })
  const { reset } = formMethods
  const [eventId] = useWatch({ control: formMethods.control, name: ['eventId'] })
  const selectedEvent = eventId ? getNestedOrFloatingElementById([currentImage], eventId.key) : null
  const parsedEvent = parseEvent({
    event: selectedEvent,
    types: zionSlsState.types,
    t,
    showRelationshipEvents: false,
    selectedRelationshipPerson: null,
    parentRelationship: null,
  })

  const resetAndClose = useCallback(() => {
    reset() // Resets the react hook form
    formMethods.setValue('eventId', null) // Resets the form value
    closeOverlay('linkExistingEvent')
  }, [reset, closeOverlay, formMethods])

  const onSubmit = (values) => {
    const event = getNestedOrFloatingElementById([currentImage], values.eventId.key)
    const dispatches = [
      {
        type: 'ADD_SUB',
        value: {
          superPartitionKey: person?.partitionKey,
          subPartitionKey: event?.partitionKey,
          superId: person?.id,
          subId: event?.id,
          elementType: 'PERSON',
        },
      },
    ]
    if (currentRecord?.id) {
      dispatches.push({
        type: 'SET_RECORD_PERSON_IDS',
        value: currentRecord.id,
      })
    }
    const sls = generateSls(dispatches)

    return { dispatches, sls }
  }

  return (
    <ZionForm methods={formMethods} onSubmit={onSubmit} id="link-existing-event">
      <InfoSheetPanel
        {...linkExistingEventOverlay}
        title={t('edit-index.edit-person.heading', 'Edit {personName}', {
          personName,
        })}
        close={resetAndClose}
        headingColor="green"
        footer={
          <SaveComponent
            saveChangesOverlay={saveChangesOverlay}
            onSubmit={onSubmit}
            finished={(dispatches) => {
              onSave(dispatches, formMethods.getValues())
              resetAndClose()
            }}
            canceled={resetAndClose}
            formName="link-existing-event"
            personId={person?.personId}
            shouldSaveToSls={shouldSaveToSls}
            appName={appName}
          />
        }
      >
        <PanelHeader
          title={t('link-existing-event.panel-heading', 'Link to an Existing Event')}
          caption={t(
            'link-existing-event.panel-caption',
            'Select an event from the dropdown to add an existing event to the record.'
          )}
        />
        <FormAutosuggest
          name="eventId"
          label={t('link-existing-event.event-field-label', 'Event')}
          suggestions={eventSuggestions}
          placeholder={t('link-existing-event.event-placeholder', 'Event')}
        />
        {selectedEvent && (
          <Event event={parsedEvent} attachedPersonIds={selectedEvent.superElements.map((supEl) => supEl.id)} />
        )}

        <Button
          linkName="Link Existing Event Open Event Panel"
          data-testid="link-existing-event-open-event-panel"
          onClick={() => {
            openOverlay('eventPanel')
          }}
        >
          {t('link-existing-event.add-event.btn', 'Event not on list')}
        </Button>

        <Tip
          primaryText={t('link-existing-event.tip.header', 'What is an event?')}
          listPrimaryText={
            <>{t('link-existing-event.tip.content', 'Events include births, deaths, marriages, burials, etc.')}</>
          }
        />
        <Tip
          primaryText={t('link-existing-event.tip2.header', 'How do I link a marriage?')}
          listPrimaryText={
            <ol>
              <li>
                {t('link-existing-event.tip2.content', 'Find a person who is already linked to the marriage event.')}
              </li>
              <Separator size="nano" />
              <li>{t('link-existing-event.tip2.content2', 'Edit the marriage event and link the missing person.')}</li>
            </ol>
          }
        />
      </InfoSheetPanel>
    </ZionForm>
  )
}

export default LinkExistingEventPanel
